<script lang="ts" setup>
  // External
  import type { SMSThreadQueryParams } from '@api/query';
  import type { CalendarDate } from '@internationalized/date';
  import { parseDate } from '@internationalized/date';
  import { useDebounceFn } from '@vueuse/core';
  import dayjs from 'dayjs';
  import { onMounted, shallowRef } from 'vue';

  // Workspace
  import { DateSelect } from '@ui/components/date';
  import { SearchBar } from '@ui/components/input';

  import { BusinessUnitFilter, CustomerFilter } from '@/components/filters';
  import { useBusinessUnits } from '@/composables/useBusinessUnits';

  defineEmits<{
    (e: 'filter', filters: SMSThreadQueryParams): void;
  }>();
  const filters = defineModel<SMSThreadQueryParams>({ required: true });

  const { hasBusinessUnits } = useBusinessUnits();
  const selectedDate = shallowRef<CalendarDate | null>(null);

  const debouncedSearch = useDebounceFn((searchStr: string) => {
    Object.assign(filters.value, {
      page: 0,
      search: searchStr,
    });
  }, 400);

  onMounted(async () => {
    if (filters.value.after) {
      selectedDate.value = parseDate(filters.value.after.format('YYYY-MM-DD'));
    }
  });
</script>

<template>
  <div class="flex justify-between w-full items-center flex-wrap gap-4">
    <div class="flex gap-2">
      <SearchBar
        placeholder="Search SMS threads..."
        :model-value="filters.search"
        class="w-full lg:w-[300px]"
        size="sm"
        @input="debouncedSearch($event.target.value)"
      />

      <CustomerFilter
        :partner-id="filters.partner"
        @update="(partner) => Object.assign(filters, { page: 0, partner: partner?.id })"
      />

      <BusinessUnitFilter
        v-if="hasBusinessUnits"
        @update="
          (ids) =>
            Object.assign(filters, {
              page: 0,
              business_unit_id__in: ids,
            })
        "
      />

      <DateSelect
        :model-value="selectedDate"
        @update:model-value="
          (d) => {
            selectedDate = d;
            Object.assign(filters, {
              after: d ? dayjs.utc(d.toString()) : undefined,
              page: 0,
            });
          }
        "
      />
    </div>

    <slot />
  </div>
</template>

<style lang="postcss" scoped></style>
